import { Input } from "@drone-js/core";
import { VStack } from "apps/commons/components/Stack";
import type { FunctionComponent } from "react";
import { useMemo, useRef, useState } from "react";
import { useLocation } from "react-router";
import { usePropertySelectionViewModelValue } from "../propertySelectionViewModelAtom.ts";
import { ValidationErrorNotification } from "./ValidationErrorNotification";
import { scrapingErrorResponseMapper } from "./scrapingErrorResponseMapper.ts";
import type { useURLValidationHandler } from "./useURLValidationHandler";

interface Props extends ReturnType<typeof useURLValidationHandler> {
    onLearnMore: () => void;
}

export const NewURLInput: FunctionComponent<Props> = ({
    input,
    setInput,
    isLoading,
    error,
    errorMessage,
    onLearnMore,
}) => {
    const {
        inputUrlCms: { emptyUrl, listingLink },
    } = usePropertySelectionViewModelValue();

    // Get error response from state
    const { state } = useLocation();
    const extendedErrorType = state?.errorResponse?.errorType;
    const [extendedErrorMessage, setExtendedErrorMessage] = useState(state?.errorResponse?.message ?? "");

    // Track if this is the first render and previous error value
    const isFirstRender = useRef(true);
    const prevError = useRef(error);

    // Compute the combined error type - prioritize current error, reset extended error when error changes
    const combinedErrorType = useMemo(() => {
        if (isFirstRender.current) {
            isFirstRender.current = false;
            prevError.current = error;
            return error ?? extendedErrorType;
        }

        if (prevError.current !== error) {
            prevError.current = error;
            return error; // Only use current error when it changes, ignore extended error
        }

        return error ?? extendedErrorType;
    }, [error, extendedErrorType]);

    return (
        <VStack gap="8">
            <Input
                label={listingLink}
                persistentLabel={false}
                value={input}
                onChange={(e) => {
                    setInput(e.currentTarget.value);
                    setExtendedErrorMessage("");
                }}
                clearLabel="Clear"
                state={isLoading ? "disabled" : "enabled"}
                data-element-name="url-input-field"
                {...(combinedErrorType === scrapingErrorResponseMapper.EmptyInput
                    ? {
                          helperText: emptyUrl,
                          intent: "error",
                      }
                    : {})}
            />

            <ValidationErrorNotification
                errorType={combinedErrorType}
                errorMessage={extendedErrorMessage ?? errorMessage ?? ""}
                onLearnMore={onLearnMore}
            />
        </VStack>
    );
};
